import React from 'react';
import BackgroundOptions from './BackgroundOptions/BackgroundOptions';
import BackgroundSelectionHeader from './BackgroundSelectionHeader/BackgroundSelectionHeader';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Drawer from '@material-ui/core/Drawer';
import Typography from '@material-ui/core/Typography';
import { makeStyles, Theme, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';

const useStyles = makeStyles((theme: Theme) => ({
	paper: {
		display: 'flex',
		flexDirection: 'column',
		width: theme.rightDrawerWidth,
		maxWidth: '100vw',
		height: `calc(100% - ${theme.footerHeight}px)`,
		borderLeft: `1px solid ${theme.palette.divider}`,
		background: theme.palette.background.paper,
		[theme.breakpoints.down('sm')]: {
			width: '100%',
			maxWidth: '100%',
			height: 'auto',
			maxHeight: '50%',
			top: 'auto',
			bottom: `calc(${theme.mobileFooterHeight}px + env(safe-area-inset-bottom))`,
			borderLeft: 0,
			borderTop: 0,
			borderRadius: '16px 16px 0 0',
			boxShadow: '0 -4px 16px rgba(0, 0, 0, 0.25)',
		},
	},
	options: {
		flex: 1,
		minHeight: 0,
		overflowY: 'auto',
		padding: '12px 16px 16px',
	},
	status: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		padding: '10px 16px',
		background: '#EAF4FB',
		borderBottom: `1px solid ${theme.palette.divider}`,
		flexShrink: 0,
	},
	doneRow: {
		flexShrink: 0,
		padding: '8px 16px 12px',
		borderTop: `1px solid ${theme.palette.divider}`,
		background: theme.palette.background.paper,
	},
	done: {
		minHeight: 48,
	},
}));

/**
 * In-call background chooser. A side panel on desktop and a bottom sheet
 * (sitting above the control bar so Leave stays reachable) on phones.
 * The setup screen shows the same options inline instead.
 */
function BackgroundSelectionDialog() {
	const classes = useStyles();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
	const {
		isBackgroundSelectionOpen,
		setIsBackgroundSelectionOpen,
		backgroundStatus,
	} = useVideoContext();
	const close = () => setIsBackgroundSelectionOpen(false);

	return (
		<Drawer
			variant="persistent"
			anchor={isMobile ? 'bottom' : 'right'}
			open={isBackgroundSelectionOpen}
			onClose={close}
			transitionDuration={0}
			classes={{ paper: classes.paper }}
			PaperProps={{
				id: 'background-panel',
				role: 'region',
				'aria-labelledby': 'background-panel-title',
			}}
		>
			<BackgroundSelectionHeader onClose={close} />
			{backgroundStatus === 'loading' && (
				<div role="status" className={classes.status}>
					<CircularProgress size={18} aria-hidden="true" />
					<Typography variant="body2" component="p">
						Preparing your background. Your camera is paused for a
						moment.
					</Typography>
				</div>
			)}
			<div className={classes.options}>
				<BackgroundOptions layout="grid" />
			</div>
			{isMobile && (
				<div className={classes.doneRow}>
					<Button
						variant="contained"
						color="primary"
						fullWidth
						className={classes.done}
						onClick={close}
					>
						Done
					</Button>
				</div>
			)}
		</Drawer>
	);
}

export default BackgroundSelectionDialog;
